import { useState, useCallback } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  orderBy, 
  deleteDoc,
  serverTimestamp,
  writeBatch,
  Bytes
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';

// Limite prudenziale per ogni documento (Firestore ha 1MB, noi usiamo 800KB)
const CHUNK_SIZE = 800 * 1024; 

export const useBackup = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper per comprimere stringa in Uint8Array
  const compressData = async (jsonString) => {
    const stream = new Blob([jsonString]).stream();
    const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
    const chunks = [];
    const reader = compressedStream.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    return new Uint8Array(await new Blob(chunks).arrayBuffer());
  };

  // Helper per decomprimere Uint8Array in stringa
  const decompressData = async (Uint8ArrayData) => {
    const stream = new Blob([Uint8ArrayData]).stream();
    const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'));
    const response = new Response(decompressedStream);
    return await response.text();
  };

  const listBackups = useCallback(async () => {
    if (!user) return [];
    try {
      setLoading(true);
      // Cerchiamo i "metadati" dei backup (i documenti principali)
      const backupsRef = collection(db, `users/${user.uid}/backups`);
      const q = query(backupsRef, orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      }));
    } catch (err) {
      console.error('Errore recupero backup:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createBackup = useCallback(async (data) => {
    if (!user) return null;
    try {
      setLoading(true);
      setError(null);

      const jsonString = JSON.stringify(data);
      const compressed = await compressData(jsonString);

      const backups = await listBackups();
      let backupId = 'backup_1';
      if (backups.length >= 2) {
        backupId = backups[backups.length - 1].id;
      } else if (backups.length === 1) {
        backupId = backups[0].id === 'backup_1' ? 'backup_2' : 'backup_1';
      }

      // Dividiamo in pezzi
      const chunks = [];
      for (let i = 0; i < compressed.length; i += CHUNK_SIZE) {
        chunks.push(compressed.slice(i, i + CHUNK_SIZE));
      }

      // Salviamo i pezzi in una sottocollezione
      const batch = writeBatch(db);
      
      // 1. Pulizia vecchi pezzi del backup specifico
      const oldChunks = await getDocs(collection(db, `users/${user.uid}/backups/${backupId}/chunks`));
      oldChunks.forEach(d => batch.delete(d.ref));

      // 2. Salvataggio nuovi pezzi
      chunks.forEach((chunk, index) => {
        const chunkRef = doc(db, `users/${user.uid}/backups/${backupId}/chunks`, `chunk_${index}`);
        batch.set(chunkRef, { content: Bytes.fromUint8Array(chunk) });
      });

      // 3. Salvataggio metadati principali
      const backupRef = doc(db, `users/${user.uid}/backups`, backupId);
      batch.set(backupRef, {
        updatedAt: serverTimestamp(),
        backupName: new Date().toLocaleDateString('it-IT'),
        chunkCount: chunks.length,
        totalSize: compressed.length
      });

      await batch.commit();
      return backupId;
    } catch (err) {
      console.error('Errore creazione backup:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, listBackups]);

  const downloadBackup = useCallback(async (backupId) => {
    if (!user) return null;
    try {
      setLoading(true);
      // Recuperiamo tutti i pezzi
      const chunksSnapshot = await getDocs(query(
        collection(db, `users/${user.uid}/backups/${backupId}/chunks`),
        orderBy('__name__', 'asc') // Ordina per nome chunk_0, chunk_1...
      ));

      if (chunksSnapshot.empty) throw new Error('Backup non trovato o vuoto');

      // Uniamo i pezzi
      const totalSize = chunksSnapshot.docs.reduce((acc, doc) => acc + doc.data().content.toUint8Array().length, 0);
      const combined = new Uint8Array(totalSize);
      let offset = 0;
      chunksSnapshot.docs.forEach(doc => {
        const chunk = doc.data().content.toUint8Array();
        combined.set(chunk, offset);
        offset += chunk.length;
      });

      const jsonString = await decompressData(combined);
      return JSON.parse(jsonString);
    } catch (err) {
      console.error('Errore download backup:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    createBackup,
    listBackups,
    downloadBackup,
    loading,
    error
  };
};
